import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NativeSelect } from '../components/ui/native-select';
import {
  Eye, Loader2, Trash2, HelpCircle, ExternalLink, SignalHigh, FlaskConical,
  CheckCircle2, AlertTriangle, PlugZap, Settings, Clock, RefreshCw, Send,
  KeyRound, ShieldCheck, ChevronDown, Plus, TrendingUp, TrendingDown,
  Router, Copy, Check, XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip } from 'react-tooltip';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useConfirm } from '../hooks/useConfirm';

const SETUP_GUIDE_URL = 'https://github.com/vishalmkatkar/jigri-trades/blob/main/docs/auto_refresh_token_setup.md';

function LiveCardSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardContent className="flex flex-col gap-3 p-4 flex-1">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-2/5" />
          <Skeleton className="h-7 w-20 rounded" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex gap-1.5 mt-auto pt-1 border-t border-border/40">
          <Skeleton className="h-7 flex-1" />
          <Skeleton className="h-7 w-8" />
        </div>
      </CardContent>
    </Card>
  );
}

function BookCard({ b, display_helm, navigate, onDelete }) {
  const ret = b.cumulative_return_pct;
  const isUp = ret != null && ret >= 0;
  const isPaused = b.status === 'paused';
  const isStopped = b.status === 'stopped';
  const noScreener = !b.active_screener_id;

  return (
    <Card className="group hover:ring-1 hover:ring-primary/20 transition-all">
      <CardContent className="flex flex-col gap-3 p-4 flex-1">

        {/* 1. Header Row */}
        <div className="flex items-start justify-between gap-2 w-full">
          <div className="min-w-0 flex-1">
            <span className="font-semibold text-base text-foreground truncate block">{b.name}</span>
            {(isPaused || isStopped || noScreener) && (
              <div className="flex gap-1.5 flex-wrap mt-1.5">
                {isPaused && (
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30 text-[9px] font-bold uppercase tracking-wide">
                    Paused
                  </span>
                )}
                {isStopped && (
                  <span className="px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-600 ring-1 ring-rose-500/30 text-[9px] font-bold uppercase tracking-wide">
                    Stopped
                  </span>
                )}
                {noScreener && (
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30 text-[9px] font-bold uppercase tracking-wide">
                    No active pair
                  </span>
                )}
              </div>
            )}
          </div>
          {display_helm(b.helm_id)}
        </div>

        {/* 2. Metrics Area */}
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xl font-bold text-foreground tabular-nums">
              ₹{b.portfolio_value?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
            {ret != null && (
              <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isUp ? 'text-win' : 'text-loss'}`}>
                {isUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {isUp ? '+' : ''}{ret.toFixed(2)}%
              </span>
            )}
          </div>
          <span className="text-muted-foreground text-xs">
            {b.open_positions ?? 0} open · {b.summary?.total_trades || 0} trades
          </span>
        </div>

        {/* 3. Actions Row */}
        <div className="flex items-center gap-2 mt-1 pt-2 border-t border-border/40">
          <Button
            variant="secondary"
            className="h-7 px-2 text-xs font-medium flex-1"
            onClick={() => navigate(`/live/${b.id}`)}
          >
            <Eye className="h-3.5 w-3.5 mr-1" /> View
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(b.id, b.name)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

      </CardContent>
    </Card>
  );
}

function BookSection({ title, icon, tint, books, emptyText, display_helm, navigate, onDelete }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <span className={`p-1.5 rounded-md border ${tint}`}>{icon}</span>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="text-[10px] font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {books.length}
        </span>
      </div>
      {books.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
          {emptyText}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {books.map(b => (
            <BookCard key={b.id} b={b} display_helm={display_helm} navigate={navigate} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function SetupHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline focus:outline-hidden"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          How do I get these?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Setting up Zerodha auto-refresh</DialogTitle>
          <DialogDescription>
            Zerodha tokens expire daily. With your API credentials plus password and TOTP
            secret, Jigri refreshes the token automatically every morning before market open.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm text-foreground">
          <div>
            <div className="font-semibold mb-1">1. Create a free Kite Personal app</div>
            <p className="text-muted-foreground">
              Register an app at{' '}
              <a href="https://kite.trade/connect" target="_blank" rel="noreferrer" className="font-mono text-primary hover:underline">kite.trade/connect</a>{' '}
              and choose <span className="font-semibold">Personal</span> as the app Type — it's free, no paid
              Connect plan needed (live quotes are provided for you).
              Copy its <span className="font-semibold">API Key</span> and{' '}
              <span className="font-semibold">API Secret</span>. Each user needs their own app.
            </p>
          </div>

          <div>
            <div className="font-semibold mb-1">2. Set the Redirect &amp; Postback URLs</div>
            <p className="text-muted-foreground mb-1.5">In the Kite app settings, enter:</p>
            <div className="flex flex-col gap-1.5">
              <div>
                <div className="text-xs text-muted-foreground">Redirect URL</div>
                <code className="text-xs break-all">{window.location.origin}/api/live/auth/callback</code>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Postback URL</div>
                <code className="text-xs break-all">{window.location.origin}/api/live/postback</code>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-1.5">
              The Redirect URL must match exactly — auto-refresh drives this login programmatically.
            </p>
          </div>

          <div>
            <div className="font-semibold mb-1">3. Find your TOTP secret</div>
            <p className="text-muted-foreground">
              In Zerodha Console → Profile → Settings → Account security, set up
              authenticator-app (TOTP) 2FA. When the QR code is shown, reveal the{' '}
              <span className="font-semibold">setup key</span> — the long alphanumeric string
              (e.g. <span className="font-mono">JBSWY3DP…</span>), <span className="font-semibold">not</span> the
              6-digit code. That secret is what you paste below.
            </p>
          </div>

          <div>
            <div className="font-semibold mb-1">4. Save &amp; verify</div>
            <p className="text-muted-foreground">
              Enter all four values and Save. Once a valid token exists you'll see
              <span className="text-win font-medium"> Auto-refresh on</span>. The API key + secret
              alone enable manual login; adding password + TOTP secret unlocks automatic refresh.
            </p>
          </div>

          <a
            href={SETUP_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Read the full setup guide
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ZerodhaAuthCard({ auth, showSetup, onToggleSetup, onLogin, onRetryRefresh }) {
  const [expanded, setExpanded] = useState(false);

  if (!auth) {
    return (
      <Card className="mb-5 p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
      </Card>
    );
  }

  const connected = auth.token_valid;
  const expired = auth.configured && !auth.token_valid;
  const notConfigured = !auth.configured;

  return (
    <Card className={`mb-5 p-4 ${expired ? 'bg-amber-400/5 border-amber-400/30' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            connected ? 'bg-win/10' : expired ? 'bg-amber-400/15' : 'bg-muted'
          }`}>
            {connected
              ? <CheckCircle2 className="h-[19px] w-[19px] text-win" strokeWidth={2.2} />
              : expired
                ? <AlertTriangle className="h-[19px] w-[19px] text-amber-600" strokeWidth={2} />
                : <PlugZap className="h-[19px] w-[19px] text-muted-foreground" strokeWidth={2} />}
          </span>
          <div>
            <div className="text-[14.5px] font-semibold text-foreground">
              {connected ? 'Zerodha connected' : expired ? 'Session expired' : 'Connect your Zerodha account'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {connected && <>User ID <span className="font-mono text-foreground font-medium">{auth.zerodha_user_id}</span></>}
              {expired && (auth.auto_refresh_configured
                ? 'Auto-refresh will retry automatically, or reconnect now.'
                : 'Reconnect to resume live trading.')}
              {notConfigured && 'Link your Kite Connect credentials to enable live trading.'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {auth.configured && (
            <Button variant="ghost" size="sm" onClick={onToggleSetup}>
              <Settings className="h-3.5 w-3.5" />
              {showSetup ? 'Cancel' : 'Setup'}
            </Button>
          )}
          {connected && (
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="sm:hidden flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
              aria-label={expanded ? 'Hide connection details' : 'Show connection details'}
              aria-expanded={expanded}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {connected && (
        <div className={`${expanded ? 'flex' : 'hidden'} sm:flex gap-2 flex-wrap mt-3.5 pt-3.5 border-t border-border`}>
          {auth.token_date && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground bg-secondary px-2.5 py-1 rounded-full">
              <Clock className="h-3 w-3" /> Token date: {auth.token_date}
            </span>
          )}
          {auth.auto_refresh_configured && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-win bg-win/10 px-2.5 py-1 rounded-full">
              <RefreshCw className="h-3 w-3" /> Auto-refresh on
            </span>
          )}
          {auth.telegram_chat_id && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-win bg-win/10 px-2.5 py-1 rounded-full">
              <Send className="h-3 w-3" /> Telegram alerts on
            </span>
          )}
        </div>
      )}

      {expired && (
        <div className="flex items-center gap-3 mt-3.5 pt-3.5 border-t border-amber-400/30">
          {auth.auto_refresh_configured ? (
            <>
              <Button size="sm" onClick={onRetryRefresh}>Retry now</Button>
              <button
                type="button"
                onClick={onLogin}
                className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline underline-offset-2"
              >
                or reconnect manually →
              </button>
            </>
          ) : (
            <Button size="sm" onClick={onLogin}>Login with Zerodha</Button>
          )}
        </div>
      )}

      {notConfigured && (
        <div className="mt-3.5 pt-3.5 border-t border-border">
          <Button size="sm" onClick={onToggleSetup}>Connect Zerodha</Button>
        </div>
      )}
    </Card>
  );
}

function BridgeCard({ bridge, installCommand, settingUp, onSetup, onDismissInstall, onRevoke, revoking }) {
  const [copied, setCopied] = useState(false);

  // Internal/exempt accounts (see INTERNAL_TRADING_EMAILS) never route
  // through a bridge — showing this card to them would be actively
  // misleading, since setting one up would have zero effect on their orders.
  if (bridge?.status === 'not_required') return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(installCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const status = bridge?.status;
  const connected = status === 'connected';
  const pending = status === 'pending';
  const offline = status === 'disconnected' || status === 'revoked';
  const notConfigured = !bridge || status === 'not_configured';

  return (
    <Card className="mb-5 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            connected ? 'bg-win/10' : (pending || offline) ? 'bg-amber-400/15' : 'bg-muted'
          }`}>
            {connected
              ? <CheckCircle2 className="h-[19px] w-[19px] text-win" strokeWidth={2.2} />
              : (pending || offline)
                ? <AlertTriangle className="h-[19px] w-[19px] text-amber-600" strokeWidth={2} />
                : <Router className="h-[19px] w-[19px] text-muted-foreground" strokeWidth={2} />}
          </span>
          <div>
            <div className="text-[14.5px] font-semibold text-foreground">
              {connected ? 'Bridge connected'
                : pending ? 'Waiting for bridge to connect'
                : offline ? 'Bridge offline'
                : 'External execution (optional)'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {connected && bridge.last_heartbeat_at &&
                `Last heartbeat ${new Date(bridge.last_heartbeat_at).toLocaleTimeString()}${bridge.bridge_version ? ` · v${bridge.bridge_version}` : ''}`}
              {pending && (installCommand
                ? 'Run the install command on your own server to complete pairing.'
                : "Lost the install command? Generate a new one below — it replaces the old one.")}
              {offline && 'Orders are paused until the bridge reconnects, or generate a new install command.'}
              {notConfigured && 'Run trades through a server you control instead of jigritrades.in\'s IP — required if your broker enforces a static-IP whitelist you manage yourself.'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!notConfigured && !pending && (
            <Button variant="ghost" size="sm" onClick={onRevoke} disabled={revoking}>
              {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
              Revoke
            </Button>
          )}
          {(notConfigured || pending || offline) && !installCommand && (
            <Button size="sm" onClick={onSetup} disabled={settingUp}>
              {settingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Router className="h-3.5 w-3.5" />}
              {notConfigured ? 'Set up bridge' : 'Generate new install command'}
            </Button>
          )}
        </div>
      </div>

      {installCommand && (
        <div className="mt-3.5 pt-3.5 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">
            Run this on the server whose IP you'll register with your broker. The command expires in 10 minutes if unused.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11.5px] font-mono bg-secondary px-3 py-2 rounded-md overflow-x-auto whitespace-nowrap">
              {installCommand}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <button
            type="button"
            onClick={onDismissInstall}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline underline-offset-2 mt-2"
          >
            Dismiss
          </button>
        </div>
      )}
    </Card>
  );
}

function ZerodhaSetupForm({ setupForm, setSetupForm, onSubmit, savingSetup, setupError, onCancel }) {
  const [autoRefreshOpen, setAutoRefreshOpen] = useState(!!(setupForm.password || setupForm.totp_secret));
  const [telegramOpen, setTelegramOpen] = useState(!!setupForm.telegram_chat_id);

  const set = (key) => (e) => setSetupForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <form onSubmit={onSubmit} className="mb-5 p-4 rounded-lg border bg-card border-border flex flex-col gap-2.5 max-w-md">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold text-foreground">Zerodha Setup</div>
        <SetupHelpDialog />
      </div>
      <p className="text-xs text-muted-foreground">Your free Kite <span className="font-semibold">Personal</span> app credentials. Register at <span className="font-mono">kite.trade/connect</span> (choose the Personal app Type) and enter the details below.</p>

      <div className="flex items-center gap-1.5 mt-1">
        <KeyRound className="h-3 w-3 text-muted-foreground" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">API credentials</span>
      </div>
      <div className="flex flex-col gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-secondary-foreground flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-primary" /> API Key
          </span>
          <Input type="text" value={setupForm.api_key} onChange={set('api_key')} className="font-mono text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-secondary-foreground flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-primary" /> API Secret
          </span>
          <Input type="password" value={setupForm.api_secret} onChange={set('api_secret')} className="font-mono text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-secondary-foreground flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-primary" /> Zerodha User Id
          </span>
          <Input type="text" value={setupForm.user_id} onChange={set('user_id')} className="font-mono text-sm" />
        </label>
      </div>

      <div className="rounded-md border border-border bg-secondary/60 p-3">
        <button type="button" onClick={() => setAutoRefreshOpen(o => !o)} className="flex items-center justify-between w-full text-left">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-secondary-foreground" />
            Auto-refresh
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-background px-1.5 py-0.5 rounded-full">Optional</span>
          </span>
          <ChevronDown className={`h-3.5 w-3.5 text-secondary-foreground transition-transform ${autoRefreshOpen ? 'rotate-180' : ''}`} />
        </button>
        {autoRefreshOpen && (
          <div className="flex flex-col gap-2 mt-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-secondary-foreground">Zerodha password</span>
              <Input type="password" value={setupForm.password} onChange={set('password')} className="font-mono text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-secondary-foreground">TOTP secret key</span>
              <Input type="password" value={setupForm.totp_secret} onChange={set('totp_secret')} className="font-mono text-sm" />
            </label>
            <p className="text-[11px] text-muted-foreground/70">TOTP secret = the alphanumeric key shown when setting up 2FA on Zerodha (not the 6-digit code). Password + TOTP enable automatic daily token refresh.</p>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-secondary/60 p-3">
        <button type="button" onClick={() => setTelegramOpen(o => !o)} className="flex items-center justify-between w-full text-left">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Send className="h-3.5 w-3.5 text-secondary-foreground" />
            Telegram alerts
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-background px-1.5 py-0.5 rounded-full">Optional</span>
          </span>
          <ChevronDown className={`h-3.5 w-3.5 text-secondary-foreground transition-transform ${telegramOpen ? 'rotate-180' : ''}`} />
        </button>
        {telegramOpen && (
          <div className="flex flex-col gap-2 mt-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-secondary-foreground">Telegram Chat ID</span>
              <Input type="text" value={setupForm.telegram_chat_id} onChange={set('telegram_chat_id')} className="font-mono text-sm" />
            </label>
            <p className="text-[11px] text-muted-foreground/70">Message <span className="font-mono">@userinfobot</span> on Telegram to get your chat id, then start a chat with the trading bot. Order events, the morning digest, and token-refresh alerts are sent here. Leave blank to disable.</p>
          </div>
        )}
      </div>

      {setupError && <div className="text-sm text-destructive">{setupError}</div>}
      <div className="flex gap-2 mt-1">
        <Button type="submit" size="sm" disabled={savingSetup}>{savingSetup ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : 'Save'}</Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

export default function LiveList() {
  const navigate = useNavigate();

  const [books, setBooks]       = useState([]);
  const [helms, setHelms]       = useState([]);
  const [auth, setAuth]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const { confirm, confirmDialog } = useConfirm();

  // Create book form
  const [creating, setCreating] = useState(false);
  const [form, setForm]         = useState({ helm_id: '', name: 'Live Book', initial_funds: 100000 });

  // Setup form (api key + secret + password + TOTP)
  const [showSetup, setShowSetup]   = useState(false);
  const [setupForm, setSetupForm]   = useState({ api_key: '', api_secret: '', user_id: '', password: '', totp_secret: '', telegram_chat_id: '' });
  const [savingSetup, setSavingSetup] = useState(false);
  const [setupError, setSetupError] = useState('');

  // External-execution bridge
  const [bridge, setBridge]           = useState(null);
  const [installCommand, setInstallCommand] = useState('');
  const [settingUpBridge, setSettingUpBridge] = useState(false);
  const [revokingBridge, setRevokingBridge]   = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/live/books'),
      api.get('/helms'),
      api.get('/live/auth/status'),
      api.get('/live/bridge/status'),
    ]).then(([booksRes, helmsRes, authRes, bridgeRes]) => {
      setBooks(booksRes.data);
      setHelms(helmsRes.data);
      setAuth(authRes.data);
      setBridge(bridgeRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Poll while pairing is in progress so "connected" shows up without a
  // manual refresh once the bridge client's heartbeat lands.
  useEffect(() => {
    if (bridge?.status !== 'pending') return;
    const id = setInterval(async () => {
      try {
        const r = await api.get('/live/bridge/status');
        setBridge(r.data);
        if (r.data.status === 'connected') setInstallCommand('');
      } catch { /* transient — next tick retries */ }
    }, 10000);
    return () => clearInterval(id);
  }, [bridge?.status]);

  const handleSetupBridge = async () => {
    setSettingUpBridge(true);
    try {
      const res = await api.post('/live/bridge/setup');
      setInstallCommand(
        `curl -sSL https://jigritrades.in/bridge/install.sh | sudo bash -s -- ${res.data.install_token}`
      );
      const r = await api.get('/live/bridge/status');
      setBridge(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set up bridge');
    } finally {
      setSettingUpBridge(false);
    }
  };

  const handleRevokeBridge = async () => {
    if (!(await confirm(
      'Live trading for this account will stop until a new bridge is paired and connected.',
      { title: 'Revoke bridge?', confirmText: 'Revoke' },
    ))) return;
    setRevokingBridge(true);
    try {
      await api.post('/live/bridge/revoke');
      const r = await api.get('/live/bridge/status');
      setBridge(r.data);
      setInstallCommand('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke bridge');
    } finally {
      setRevokingBridge(false);
    }
  };

  const refreshAuth = async () => {
    const r = await api.get('/live/auth/status');
    setAuth(r.data);
  };

  const handleSaveSetup = async (e) => {
    e.preventDefault();
    setSavingSetup(true);
    setSetupError('');
    const payload = {};
    if (setupForm.api_key.trim())     payload.api_key     = setupForm.api_key.trim();
    if (setupForm.api_secret.trim())  payload.api_secret  = setupForm.api_secret.trim();
    if (setupForm.user_id.trim())    payload.user_id    = setupForm.user_id.trim();
    if (setupForm.password.trim())    payload.password    = setupForm.password.trim();
    if (setupForm.totp_secret.trim()) payload.totp_secret = setupForm.totp_secret.trim();
    if (setupForm.telegram_chat_id.trim()) payload.telegram_chat_id = setupForm.telegram_chat_id.trim();
    try {
      await api.post('/live/auth/setup', payload);
      await refreshAuth();
      setShowSetup(false);
      setSetupForm({ api_key: '', api_secret: '', user_id: '', password: '', totp_secret: '', telegram_chat_id: '' });
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setSavingSetup(false);
    }
  };

  const handleLoginZerodha = async () => {
    try {
      const res = await api.get('/live/auth/login-url');
      window.location.href = res.data.login_url;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to get login URL');
    }
  };

  const handleRefreshToken = async () => {
    try {
      await api.post('/live/auth/refresh-token');
      await refreshAuth();
    } catch (err) {
      setError(err.response?.data?.error || 'Token refresh failed');
    }
  };

  const handleToggleSetup = () => setShowSetup(s => {
    // Prefill the (non-secret) chat id when opening so the user can see/edit it.
    if (!s) setSetupForm(f => ({ ...f, telegram_chat_id: auth?.telegram_chat_id || '' }));
    return !s;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.helm_id) return;
    setCreating(true);
    setError('');
    try {
      const res = await api.post('/live/books', {
        helm_id: parseInt(form.helm_id),
        name: form.name.trim() || 'Live Book',
        initial_funds: parseFloat(form.initial_funds) || 100000,
      });
      navigate(`/live/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create live book');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!(await confirm(`This will permanently delete "${name}" and all its trade history.`, {
      title: 'Delete live book?', confirmText: 'Delete',
    }))) return;
    await api.delete(`/live/books/${id}`);
    setBooks(prev => prev.filter(b => b.id !== id));
  };

  const display_helm = (helm_id) => {
    const match_helm = helms.find((i) => i.id === helm_id);
    if (match_helm) {
      const tooltip_id = `helm-link-${match_helm.id}`;
      return <>
        <button
          type="button"
          data-tooltip-id={tooltip_id}
          onClick={() => navigate(`/helms/${match_helm.id}`)}
          className="shrink-0 max-w-[120px] truncate px-2 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          {match_helm.name}
        </button>
        <Tooltip id={tooltip_id} content='Linked helm' />
      </>;
    }
    return null;
  };

  return (
    <AppLayout>
      <main className="md:p-6 max-w-7xl mx-auto">

        {/* Zerodha auth status card */}
        <ZerodhaAuthCard
          auth={auth}
          showSetup={showSetup}
          onToggleSetup={handleToggleSetup}
          onLogin={handleLoginZerodha}
          onRetryRefresh={handleRefreshToken}
        />

        {/* External-execution bridge — only meaningful once Kite creds exist */}
        {auth?.configured && (
          <BridgeCard
            bridge={bridge}
            installCommand={installCommand}
            settingUp={settingUpBridge}
            onSetup={handleSetupBridge}
            onDismissInstall={() => setInstallCommand('')}
            onRevoke={handleRevokeBridge}
            revoking={revokingBridge}
          />
        )}

        {/* Per-user credentials form: API key + secret + password + TOTP */}
        {showSetup && (
          <ZerodhaSetupForm
            setupForm={setupForm}
            setSetupForm={setSetupForm}
            onSubmit={handleSaveSetup}
            savingSetup={savingSetup}
            setupError={setupError}
            onCancel={() => setShowSetup(false)}
          />
        )}

        <Card className="p-4 mb-5">
          <h2 className="text-base font-semibold text-foreground mb-3.5">Trading</h2>
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
            <div className="flex flex-col gap-1.5 flex-1 min-w-[160px]">
              <label className="text-xs font-medium text-secondary-foreground">Strategy</label>
              <NativeSelect
                value={form.helm_id}
                onChange={e => setForm(f => ({ ...f, helm_id: e.target.value }))}
                required
                className="h-9"
              >
                <option value="">Select Helm…</option>
                {helms.map(h => <option key={h.id} value={h.id}>{h.name}{h.is_global && !h.is_owner ? ' (Global)' : ''}</option>)}
              </NativeSelect>
            </div>
            <div className="flex flex-col gap-1.5 flex-1 min-w-[130px]">
              <label className="text-xs font-medium text-secondary-foreground">Book name</label>
              <Input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="h-9 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5 flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-secondary-foreground">Starting capital</label>
              <div className="flex items-center h-9 rounded-md border border-border bg-background overflow-hidden focus-within:ring-1 focus-within:ring-ring">
                <span className="pl-3 text-sm text-muted-foreground">₹</span>
                <input
                  type="number"
                  min={1000}
                  value={form.initial_funds}
                  onChange={e => setForm(f => ({ ...f, initial_funds: e.target.value }))}
                  className="flex-1 h-full min-w-0 border-0 bg-transparent px-2 text-sm text-foreground outline-none"
                />
              </div>
            </div>
            <Button type="submit" size="sm" disabled={creating || !form.helm_id} className="h-9 w-full sm:w-auto shrink-0">
              {creating ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Creating…</> : <><Plus className="h-3.5 w-3.5" />New book</>}
            </Button>
          </form>
        </Card>
        {error && <div className="text-sm text-destructive mb-3">{error}</div>}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => <LiveCardSkeleton key={i} />)}
          </div>
        ) : books.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">No live books yet. Create one above.</div>
        ) : (
          <>
            <BookSection
              title="Live"
              icon={<SignalHigh className="h-3.5 w-3.5" />}
              tint="bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
              books={books.filter(b => !b.is_dry_run)}
              emptyText="No real (live) books yet."
              display_helm={display_helm}
              navigate={navigate}
              onDelete={handleDelete}
            />
            <BookSection
              title="Paper"
              icon={<FlaskConical className="h-3.5 w-3.5" />}
              tint="bg-blue-500/10 text-blue-500 border-blue-500/20"
              books={books.filter(b => b.is_dry_run)}
              emptyText="No paper books yet."
              display_helm={display_helm}
              navigate={navigate}
              onDelete={handleDelete}
            />
          </>
        )}
      </main>
      {confirmDialog}
    </AppLayout>
  );
}
